import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertCircle, Info, Check, Calendar, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import { logActivity } from '@/lib/activityLog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Reusable Score Select Component
const ScoreSelect = ({ value, onChange, options, testId }) => {
  return (
    <div className="grid grid-cols-5 gap-2">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          data-testid={`${testId}-${option.value}`}
          className={`p-3 rounded-xl border-2 transition-all text-center ${
            value === option.value
              ? 'border-[#1a61e8] bg-[#1a61e8] text-white'
              : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
          }`}
        >
          <div className="text-xl font-bold mb-1">{option.value}</div>
          <div className="text-xs font-medium">{option.label}</div>
        </button>
      ))}
    </div>
  );
};

export default function WeeklyCheckin({ user, onLogout }) {
  const navigate = useNavigate();
  const [windowStatus, setWindowStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [existingCheckin, setExistingCheckin] = useState(null);
  const [formData, setFormData] = useState({
    weight: '',
    cycle_status: '',  // NEW: Cycle Status (A, B, C, D, E)
    consistency: 3,
    hunger: 3,  // 1-5 scale (1 = starving/dizzy, 5 = satisfied/no issues)
    energy: 3,  // 1-5 scale (1 = exhausted, 5 = energized)
    notes: '',
    photos: [],
    measurements: {},
    recipe_change_requested: false,
    recipe_change_details: '',
    // Phase 1: New fields (all use 1-5 scale where 1 = bad, 5 = good)
    weekend_adherence: 80,  // 0-100%
    stress_level: 3,  // 1-5 scale (1 = very stressed, 5 = calm)
    luteal_phase: null,  // Deprecated - use cycle_status instead
    medication_changed: false,
    sleep_quality: 3  // 1-5 scale (1 = poor sleep, 5 = great sleep)
  });
  const [checkinHistory, setCheckinHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    logActivity('checkin_page_viewed');
    fetchWindowStatus();
    fetchSubscription();
    fetchCheckinHistory();
  }, []);

  const fetchCheckinHistory = async () => {
    try {
      const response = await axios.get(`${API}/checkin/history`);
      setCheckinHistory(response.data || []);
    } catch (error) {
      console.log('Could not fetch check-in history');
    }
  };

  const fetchWindowStatus = async () => {
    try {
      const response = await axios.get(`${API}/checkin/window-status`);
      setWindowStatus(response.data);
      
      // Don't show toast - we'll show a proper UI message instead
      
      // After window status is loaded, check for existing check-in
      await checkExistingCheckinWithWeek(response.data.week_start_date);
    } catch (error) {
      toast.error('Failed to load check-in status');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await axios.get(`${API}/client/dashboard`);
      setSubscription(response.data.subscription);
    } catch (error) {
      console.error('Failed to load subscription');
    }
  };

  const checkExistingCheckinWithWeek = async (weekStartDate) => {
    try {
      const response = await axios.get(`${API}/checkins`);
      const checkins = response.data || [];
      
      // Get current week's check-in
      const existing = checkins.find(c => c.week_start_date === weekStartDate);
      if (existing) {
        setExistingCheckin(existing);
        // Pre-fill form with existing data
        // Convert old 1-10 values to 1-5 if they exist (divide by 2 and round)
        const convertTo5Scale = (val, defaultVal = 3) => {
          if (!val) return defaultVal;
          if (val <= 5) return val; // Already on 1-5 scale
          return Math.round(val / 2); // Convert from 1-10 to 1-5
        };
        
        setFormData({
          weight: existing.weight,
          cycle_status: existing.cycle_status || '',
          consistency: existing.consistency,
          hunger: convertTo5Scale(existing.hunger, 3),
          energy: convertTo5Scale(existing.energy, 3),
          notes: existing.notes || '',
          photos: existing.photos || [],
          measurements: existing.measurements_json || {},
          recipe_change_requested: existing.recipe_change_requested || false,
          recipe_change_details: existing.recipe_change_details || '',
          // Phase 1: New fields
          weekend_adherence: existing.weekend_adherence || 80,
          stress_level: convertTo5Scale(existing.stress_level, 3),
          luteal_phase: existing.luteal_phase || null,
          medication_changed: existing.medication_changed || false,
          sleep_quality: convertTo5Scale(existing.sleep_quality, 3)
        });
      }
    } catch (error) {
      console.error('Failed to check existing check-in');
    }
  };

  const getDaysSinceStart = () => {
    if (!subscription?.started_at) return 0;
    const start = new Date(subscription.started_at);
    const now = new Date();
    return Math.floor((now - start) / (1000 * 60 * 60 * 24));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Only enforce window check for non-test clients
    const isTestClient = user?.email?.startsWith('test_') || false;
    if (!isTestClient && !windowStatus?.is_open) {
      toast.error('Check-in window is closed');
      return;
    }

    if (!formData.weight) {
      toast.error('Weight is required');
      return;
    }

    // Accept both "83.4" and "83,4" — normalize comma to period before sending
    const normalizedWeight = formData.weight.toString().replace(',', '.').trim();
    const parsedWeight = parseFloat(normalizedWeight);
    if (isNaN(parsedWeight) || parsedWeight <= 0) {
      toast.error('Please enter a valid weight');
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${API}/checkins`, { ...formData, weight: parsedWeight });
      toast.success(existingCheckin ? 'Check-in updated successfully!' : 'Weekly check-in submitted successfully!');
      navigate('/app');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit check-in');
    } finally {
      setSubmitting(false);
    }
  };

  const daysSinceStart = getDaysSinceStart();
  const canRequestRecipeChange = daysSinceStart > 3;

  if (loading) {
    return <div data-testid="loading">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="page-container max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="checkin-page-title">
            Weekly Check-In
          </h1>
          <p className="text-lg text-gray-600">
            Week starting: {windowStatus?.week_start_date}
          </p>
          {existingCheckin && (
            <div className="mt-3 inline-flex items-center gap-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-lg">
              <Check className="h-4 w-4" />
              <span className="font-medium">Already submitted - You can edit until the window closes</span>
            </div>
          )}
        </div>

        {!windowStatus?.is_open && !user?.email?.startsWith('test_') ? (
          <div className="space-y-6">
            {/* Window Closed Message */}
            <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center">
                    <Calendar className="h-7 w-7 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-gray-900">
                      {windowStatus?.is_eligible === false ? 
                        'Your first check-in starts soon!' : 
                        'Check-in opens Wednesday'}
                    </h3>
                    <p className="text-gray-600">
                      {windowStatus?.is_eligible === false ? 
                        `You'll be able to check in starting ${windowStatus?.first_eligible_week ? new Date(windowStatus.first_eligible_week).toLocaleDateString('en-US', { month: 'long', day: 'numeric' }) : 'next week'}. This gives you time to settle in and establish a baseline.` :
                        'Weekly check-ins are available from Wednesday 00:00 until Thursday 23:59 (CET)'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Check-in History */}
            {checkinHistory.length > 0 && (
              <Card>
                <CardHeader 
                  className="cursor-pointer"
                  onClick={() => setShowHistory(!showHistory)}
                >
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-gray-500" />
                      Check-in History
                    </CardTitle>
                    {showHistory ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
                  </div>
                </CardHeader>
                {showHistory && (
                  <CardContent>
                    <div className="space-y-3">
                      {checkinHistory.map((checkin, idx) => (
                        <div 
                          key={checkin.id || idx} 
                          className="p-4 bg-gray-50 rounded-lg border border-gray-100"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-gray-900">Week of {checkin.week_start_date}</span>
                            <span className="text-sm text-green-600 flex items-center gap-1">
                              <Check className="h-4 w-4" /> Completed
                            </span>
                          </div>
                          <div className="grid grid-cols-3 gap-4 text-sm text-gray-600">
                            <div>
                              <span className="text-gray-400">Weight:</span> {checkin.weight} kg
                            </div>
                            <div>
                              <span className="text-gray-400">Consistency:</span> {checkin.consistency}/5
                            </div>
                            <div>
                              <span className="text-gray-400">Energy:</span> {checkin.energy > 5 ? Math.round(checkin.energy / 2) : checkin.energy}/5
                            </div>
                          </div>
                          {checkin.notes && (
                            <p className="mt-2 text-sm text-gray-500 italic">"{checkin.notes}"</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            )}
          </div>
        ) : (
          <>
          <form onSubmit={handleSubmit}>
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="h-5 w-5 text-blue-600" />
                  Important Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">
                  Please answer honestly and accurately. Your responses help your coach provide the best guidance for your journey.
                  You can edit your submission until the check-in window closes.
                </p>
              </CardContent>
            </Card>

            {/* Question 1: Current Weight */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>1. Current Weight</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label htmlFor="weight">Weight (kg)</Label>
                  <Input
                    id="weight"
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]+([.,][0-9]+)?"
                    placeholder="e.g. 83.4 or 83,4"
                    value={formData.weight}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^0-9.,]/g, '');
                      const firstSep = v.search(/[.,]/);
                      const cleaned = firstSep === -1
                        ? v
                        : v.slice(0, firstSep + 1) + v.slice(firstSep + 1).replace(/[.,]/g, '');
                      setFormData({...formData, weight: cleaned});
                    }}
                    required
                    data-testid="weight-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Question 2: Cycle Status (Only for female clients) */}
            {user?.gender === 'Female' && (
              <Card className="mb-6 border-purple-100 bg-purple-50/30">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    2. Cycle Status <span className="text-sm font-normal text-gray-500">(Optional)</span>
                  </CardTitle>
                  <CardDescription>
                    We track this to distinguish between fat gain and hormonal water retention so we don't lower your calories unnecessarily.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-2">
                    {[
                      { value: 'A', label: 'Follicular / Normal', sublabel: 'Weeks 1-2 of cycle' },
                      { value: 'B', label: 'Ovulation', sublabel: 'Week 3 of cycle' },
                      { value: 'C', label: 'Luteal Phase', sublabel: 'Week 4 - Period due soon' },
                      { value: 'D', label: 'Menstruating', sublabel: 'Currently bleeding' },
                      { value: 'E', label: 'Prefer not to say', sublabel: '' },
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setFormData({...formData, cycle_status: option.value})}
                        data-testid={`cycle-status-${option.value}`}
                        className={`p-3 rounded-xl border-2 transition-all text-left ${
                          formData.cycle_status === option.value
                            ? 'border-purple-500 bg-purple-100'
                            : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                            formData.cycle_status === option.value
                              ? 'border-purple-500 bg-purple-500'
                              : 'border-gray-300'
                          }`}>
                            {formData.cycle_status === option.value && (
                              <Check className="w-4 h-4 text-white" />
                            )}
                          </div>
                          <div>
                            <div className="font-medium">{option.value}) {option.label}</div>
                            {option.sublabel && (
                              <div className="text-xs text-gray-500">{option.sublabel}</div>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Question: Consistency */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>{user?.gender === 'Female' ? '3' : '2'}. How consistent were you this week?</CardTitle>
                <CardDescription>How well did you follow your meal plan?</CardDescription>
              </CardHeader>
              <CardContent>
                <ScoreSelect
                  value={formData.consistency}
                  onChange={(val) => setFormData({...formData, consistency: val})}
                  testId="consistency"
                  options={[
                    { value: 1, label: '0-20%' },
                    { value: 2, label: '20-40%' },
                    { value: 3, label: '40-70%' },
                    { value: 4, label: '70-90%' },
                    { value: 5, label: '90-100%' },
                  ]}
                />
              </CardContent>
            </Card>

            {/* Question: Hunger Levels (Renamed from Satisfaction) */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>{user?.gender === 'Female' ? '4' : '3'}. How were your hunger levels on average?</CardTitle>
                <CardDescription>Rate your average hunger throughout the week</CardDescription>
              </CardHeader>
              <CardContent>
                <ScoreSelect
                  value={formData.hunger}
                  onChange={(val) => setFormData({...formData, hunger: val})}
                  testId="hunger"
                  options={[
                    { value: 1, label: 'Starving' },
                    { value: 2, label: 'V. Hungry' },
                    { value: 3, label: 'Okay' },
                    { value: 4, label: 'Good' },
                    { value: 5, label: 'Satisfied' },
                  ]}
                />
                <div className="mt-3 text-xs text-gray-500 grid grid-cols-5 text-center">
                  <span>Dizzy/Always hungry</span>
                  <span>Energy low</span>
                  <span>Manageable</span>
                  <span>Comfortable</span>
                  <span>Full/No issues</span>
                </div>
              </CardContent>
            </Card>

            {/* Question: Energy */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>{user?.gender === 'Female' ? '5' : '4'}. How was your energy this week?</CardTitle>
                <CardDescription>Average energy level throughout the week</CardDescription>
              </CardHeader>
              <CardContent>
                <ScoreSelect
                  value={formData.energy}
                  onChange={(val) => setFormData({...formData, energy: val})}
                  testId="energy"
                  options={[
                    { value: 1, label: 'Exhausted' },
                    { value: 2, label: 'Low' },
                    { value: 3, label: 'Okay' },
                    { value: 4, label: 'Good' },
                    { value: 5, label: 'Energized' },
                  ]}
                />
              </CardContent>
            </Card>

            {/* Question: Weekend Adherence */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>{user?.gender === 'Female' ? '6' : '5'}. Weekend Adherence</CardTitle>
                <CardDescription>How consistent were you specifically on Saturday & Sunday?</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-5 gap-2">
                  {[
                    { value: 20, label: '0-20%' },
                    { value: 40, label: '20-40%' },
                    { value: 60, label: '40-60%' },
                    { value: 80, label: '60-80%' },
                    { value: 100, label: '80-100%' },
                  ].map((option, index) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData({...formData, weekend_adherence: option.value})}
                      className={`p-3 rounded-xl border-2 transition-all text-center ${
                        formData.weekend_adherence === option.value
                          ? 'border-[#1a61e8] bg-[#1a61e8] text-white'
                          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="text-xl font-bold mb-1">{index + 1}</div>
                      <div className="text-xs font-medium">{option.label}</div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Question: Stress Level */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>{user?.gender === 'Female' ? '7' : '6'}. Stress Level</CardTitle>
                <CardDescription>How calm or stressed have you felt this week?</CardDescription>
              </CardHeader>
              <CardContent>
                <ScoreSelect
                  value={formData.stress_level}
                  onChange={(val) => setFormData({...formData, stress_level: val})}
                  testId="stress"
                  options={[
                    { value: 1, label: 'Very stressed' },
                    { value: 2, label: 'Stressed' },
                    { value: 3, label: 'Some stress' },
                    { value: 4, label: 'Relaxed' },
                    { value: 5, label: 'Very calm' },
                  ]}
                />
              </CardContent>
            </Card>

            {/* Question: Sleep Quality */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>{user?.gender === 'Female' ? '8' : '7'}. Sleep Quality</CardTitle>
                <CardDescription>How well did you sleep this week on average?</CardDescription>
              </CardHeader>
              <CardContent>
                <ScoreSelect
                  value={formData.sleep_quality}
                  onChange={(val) => setFormData({...formData, sleep_quality: val})}
                  testId="sleep"
                  options={[
                    { value: 1, label: 'Terrible' },
                    { value: 2, label: 'Poor' },
                    { value: 3, label: 'Okay' },
                    { value: 4, label: 'Good' },
                    { value: 5, label: 'Great' },
                  ]}
                />
              </CardContent>
            </Card>

            {/* Question: Medication Changed */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>{user?.gender === 'Female' ? '9' : '8'}. Medication Changes</CardTitle>
                <CardDescription>Have you started, stopped, or changed any medications this week that might affect weight?</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center space-x-4">
                  <Button
                    type="button"
                    variant={formData.medication_changed === true ? 'default' : 'outline'}
                    onClick={() => setFormData({...formData, medication_changed: true})}
                    className="flex-1"
                  >
                    {formData.medication_changed === true && <Check className="mr-2 h-4 w-4" />}
                    Yes
                  </Button>
                  <Button
                    type="button"
                    variant={formData.medication_changed === false ? 'default' : 'outline'}
                    onClick={() => setFormData({...formData, medication_changed: false})}
                    className="flex-1"
                  >
                    {formData.medication_changed === false && <Check className="mr-2 h-4 w-4" />}
                    No
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Question 10: Notes */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>{user?.gender === 'Female' ? '10' : '9'}. Any extra notes?</CardTitle>
                <CardDescription>Optional - Add any context or comments about your week</CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="E.g., had a stressful week, traveled, special event..."
                  rows={4}
                />
              </CardContent>
            </Card>

            {canRequestRecipeChange && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Recipe Change Request</CardTitle>
                  <CardDescription>You can request one recipe change per week</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="recipe_change"
                      checked={formData.recipe_change_requested}
                      onCheckedChange={(checked) => setFormData({...formData, recipe_change_requested: checked})}
                      data-testid="recipe-change-checkbox"
                    />
                    <Label htmlFor="recipe_change" className="cursor-pointer">
                      I would like to request a recipe change
                    </Label>
                  </div>
                  
                  {formData.recipe_change_requested && (
                    <div className="space-y-2">
                      <Label htmlFor="recipe_details">Recipe Change Details</Label>
                      <Textarea
                        id="recipe_details"
                        placeholder="Which recipe would you like to change and why?"
                        value={formData.recipe_change_details}
                        onChange={(e) => setFormData({...formData, recipe_change_details: e.target.value})}
                        data-testid="recipe-details-textarea"
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-lg py-6"
              disabled={submitting || (!windowStatus?.is_open && !user?.email?.startsWith('test_'))}
              data-testid="submit-checkin-button"
            >
              {submitting ? (existingCheckin ? 'Updating...' : 'Submitting...') : (existingCheckin ? 'Update Check-In' : 'Submit Check-In')}
            </Button>
          </form>

          {/* Check-in History (also shown when window is open) */}
          {checkinHistory.length > 0 && (
            <Card className="mt-8">
              <CardHeader 
                className="cursor-pointer"
                onClick={() => setShowHistory(!showHistory)}
              >
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="h-5 w-5 text-gray-500" />
                    Past Check-ins
                  </CardTitle>
                  {showHistory ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
                </div>
              </CardHeader>
              {showHistory && (
                <CardContent>
                  <div className="space-y-3">
                    {checkinHistory.filter(c => c.week_start_date !== windowStatus?.week_start_date).map((checkin, idx) => (
                      <div 
                        key={checkin.id || idx} 
                        className="p-4 bg-gray-50 rounded-lg border border-gray-100"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-gray-900">Week of {checkin.week_start_date}</span>
                          <span className="text-sm text-green-600 flex items-center gap-1">
                            <Check className="h-4 w-4" /> Completed
                          </span>
                        </div>
                        <div className="grid grid-cols-3 gap-4 text-sm text-gray-600">
                          <div>
                            <span className="text-gray-400">Weight:</span> {checkin.weight} kg
                          </div>
                          <div>
                            <span className="text-gray-400">Consistency:</span> {checkin.consistency}/5
                          </div>
                          <div>
                            <span className="text-gray-400">Energy:</span> {checkin.energy > 5 ? Math.round(checkin.energy / 2) : checkin.energy}/5
                          </div>
                        </div>
                        {checkin.notes && (
                          <p className="mt-2 text-sm text-gray-500 italic">"{checkin.notes}"</p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          )}
        </>
        )}
      </div>
    </div>
  );
}
